import React from 'react';
import { Calendar, Paperclip, Star, User, UserX, Archive } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Panneau de filtres de la liste des emails (statut, dates, options, reset).
const MailEmailFilters = ({ filters, updateFilters, hasActiveFilters, resetFilters }) => {
  const { t } = useTranslation('admin');
  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
      {/* Read Status Filter */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-2">
          {t('mail.filterReadStatus')}
        </label>
        <div className="flex space-x-2">
          <button
            data-testid="mail-filter-readstatus-all"
            onClick={() => updateFilters({...filters, readStatus: 'all'})}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.readStatus === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
            }`}
          >
            {t('mail.filterAll')}
          </button>
          <button
            data-testid="mail-filter-readstatus-unread"
            onClick={() => updateFilters({...filters, readStatus: 'unread'})}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.readStatus === 'unread'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
            }`}
          >
            Non lus
          </button>
          <button
            data-testid="mail-filter-readstatus-read"
            onClick={() => updateFilters({...filters, readStatus: 'read'})}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.readStatus === 'read'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
            }`}
          >
            {t('mail.filterRead')}
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-2">
          <Calendar size={14} className="inline mr-1" />
          {t('mail.filterDateRange')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-600 mb-1">{t('mail.filterFrom')}</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => updateFilters({...filters, dateFrom: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">{t('mail.filterTo')}</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => updateFilters({...filters, dateTo: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Other Filters */}
      <div className="space-y-2">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.hasAttachments}
            onChange={(e) => updateFilters({...filters, hasAttachments: e.target.checked})}
            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
          />
          <Paperclip size={14} className="text-slate-600" />
          <span className="text-sm text-slate-700">{t('mail.filterWithAttachments')}</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.isImportant}
            onChange={(e) => updateFilters({...filters, isImportant: e.target.checked})}
            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
          />
          <Star size={14} className="text-slate-600" />
          <span className="text-sm text-slate-700">{t('mail.filterImportant')}</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.linkedToCRM}
            onChange={(e) => updateFilters({...filters, linkedToCRM: e.target.checked, notLinkedToCRM: false})}
            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
          />
          <User size={14} className="text-slate-600" />
          <span className="text-sm text-slate-700">{t('mail.filterLinkedCrm')}</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.notLinkedToCRM}
            onChange={(e) => updateFilters({...filters, notLinkedToCRM: e.target.checked, linkedToCRM: false})}
            className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
          />
          <UserX size={14} className="text-orange-600" />
          <span className="text-sm text-slate-700">{t('mail.filterNotLinkedCrm')}</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.includeArchived}
            onChange={(e) => updateFilters({...filters, includeArchived: e.target.checked})}
            className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500"
          />
          <Archive size={14} className="text-purple-600" />
          <span className="text-sm text-slate-700">{t('mail.filterIncludeArchived')}</span>
        </label>
      </div>

      {/* Reset Button */}
      {hasActiveFilters() && (
        <button
          data-testid="mail-filters-reset"
          onClick={resetFilters}
          className="w-full px-3 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm font-medium"
        >
          {t('mail.filterReset')}
        </button>
      )}
    </div>
  );
};

export default MailEmailFilters;
